import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { BrandRule } from '../../../components/brand/BrandRule';

/**
 * Shared building blocks for the 2026 (direction 3a) homepage sections so every
 * band shares the same rhythm, eyebrow style, and heading type.
 */

// Section title + optional "see all" link. Deliberately plain (2026-09): no
// eyebrow labels or taglines — just the heading, for a simple, professional page.
export function SectionHeading({
  title,
  action,
  isRTL,
  tone = 'ink',
}: {
  title: ReactNode;
  action?: { label: string; to: string };
  isRTL: boolean;
  /** 'ink' on white bands, 'light' on deep-green bands. */
  tone?: 'ink' | 'light';
}) {
  // Brand teal, not ink. The page is white throughout now, so the heading is
  // the main place the green lives; #004D40 is 9.83:1 on white.
  //
  // The TOKEN, not the hex. index.css's .dark layer overrides the named
  // utilities (.dark .text-gray-900 and friends) but cannot reach an arbitrary
  // text-[#hex], so a hardcoded teal heading would stay #004D40 on the dark
  // surface and go unreadable. --pv-color-primary is #004D40 in light -
  // identical to what shipped - and #3FBBA2 in dark (7.46:1 on the dark
  // surface). This is the same token the 209 h1/h2 headings across the rest of
  // the site now use, so structure reads teal everywhere, not just on home.
  const titleColor = tone === 'light' ? 'text-white' : 'text-[var(--pv-color-primary)]';
  const arrow = isRTL ? 'ri-arrow-left-line' : 'ri-arrow-right-line';
  return (
    <div className="mb-8 flex flex-col gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-2xl">
        <h2 className={`text-balance font-display text-2xl font-bold leading-tight ${titleColor} sm:text-3xl`}>
          {title}
        </h2>
        <BrandRule tone={tone} className="mt-3" />
      </div>
      {action && (
        // "See all" is RED — THEME.md §2.4c R3. It is the register's
        // navigate-onward mark, and it is the board's own habit: the domain
        // line closes with a red cursor arrow. A bordered green pill (what
        // this was) competed with the primary action on the same band.
        <Link
          to={action.to}
          className={`group inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 ${
            tone === 'light'
              ? 'text-[#FF8A80] hover:text-white focus-visible:ring-[#FF8A80]'
              : 'text-[#DF2F2B] hover:text-[#C62828] focus-visible:ring-[#DF2F2B]'
          }`}
        >
          {action.label}
          <i className={`${arrow} text-base transition-transform group-hover:translate-x-0.5`} aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

// The brand rule now lives in components/brand/BrandRule.tsx so every page can
// reach it, not just the homepage. Re-exported here because seven home sections
// already import it from this module.
export { BrandRule, BrandSignature } from '../../../components/brand/BrandRule';

/** Fade-up on scroll, respecting reduced-motion. */
export function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 20 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}
